<template>
    <div :class="`multiple-choice multiple-choice-align-${align}`">
        <label :for="componentId" class="field-label">
            {{ fieldLabel }}
            <span v-if="isRequired" class="required-indicator">*</span>
        </label>
        <div class="input-container">
        <div class="checkbox-group">
            <div v-for="(item, index) in items" :key="itemIds[index]" class="checkbox-wrapper">
                <input
                    type="checkbox"
                    :id="itemIds[index]"
                    :name="componentId"
                    :value="item"
                    :checked="selectedValues.some(val => val.value === item && val.id === itemIds[index])"
                    @change="updateSelection(item, $event, itemIds[index])"
                        @focus="onFocus"
                        @blur="onBlur"
                />
                    <label :for="itemIds[index]" class="checkbox-label" v-html="processItem(item)"></label>
            </div>
            <div v-if="otherOption" class="checkbox-wrapper">
                <input
                    type="checkbox"
                    :id="`other-${componentId}`"
                    :name="componentId"
                    value="other"
                    v-model="hasOtherSelected"
                        @change="handleOtherChange"
                        @focus="onFocus"
                        @blur="onBlur"
                />
                    <label :for="`other-${componentId}`" class="checkbox-label">{{ otherOption }}</label>
                <input
                    v-show="hasOtherSelected"
                    type="text"
                    v-model="otherText"
                    @input="handleOtherTextChange"
                        @focus="onFocus"
                        @blur="onBlur"
                    class="other-input"
                    placeholder="Diğer seçeneği girin"
                />
                </div>
            </div>
            <div v-if="showError && isRequired && selectedValues.length === 0" class="error-message">
                {{ fieldLabel }} alanından en az bir seçenek seçmelisiniz
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "MultipleSelection",
    props: {
        fieldLabel: {
            type: String,
            required: true,
        },
        align: {
            type: String,
            default: "top",
        },
        items: {
            type: Array,
            required: true,
            default: () => [],
        },
        isRequired: {
            type: Boolean,
            default: false,
        },
        value: {
            type: Array,
            default: () => [],
        },
        showError: {
            type: Boolean,
            default: false,
        },
        formId: {
            type: String,
            required: true,
        },
        order: {
            type: Number,
            required: true,
        },
        otherOption: {
            type: String,
            default: null,
        }
    },
    data() {
        return {
            selectedValues: [],
            otherText: "",
            hasOtherSelected: false,
            componentId: `multiple-choice-${Math.random().toString(36).substring(2, 15)}`,
            itemIds: []
        };
    },
    computed: {
        showAsterisk() {
            return this.isRequired && this.showError && this.selectedValues.length === 0;
        }
    },
    methods: {
        generateItemIds() {
            this.itemIds = this.items.map(() => `checkbox-${this.componentId}-${Math.random().toString(36).substring(2, 15)}`);
        },
        updateSelection(item, event, itemId) {
            const isChecked = event.target.checked;
            if (isChecked) {
                this.selectedValues.push({
                    value: item,
                    id: itemId
                });
            } else {
                this.selectedValues = this.selectedValues.filter(val => val.id !== itemId);
            }
            this.emitSelected();
        },
        emitSelected() {
            let values = this.selectedValues.map(item => item.value);
            if (this.hasOtherSelected && this.otherText) {
                values.push(this.otherText);
            }
            this.$emit("input", values);
            
            const hasError = this.isRequired && values.length === 0;
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: hasError
            });

            this.saveToLocalStorage();
        },
        saveToLocalStorage() {
            const multipleSelectionData = {
                selectedValues: this.selectedValues,
                hasOtherSelected: this.hasOtherSelected,
                otherText: this.otherText,
                fieldLabel: this.fieldLabel
            };
            localStorage.setItem(`${this.formId}-${this.order}`, JSON.stringify(multipleSelectionData));
        },
        loadValues() {
            const savedData = localStorage.getItem(`${this.formId}-${this.order}`);
            if (savedData) {
                const parsedData = JSON.parse(savedData);
                this.selectedValues = parsedData.selectedValues || [];
                this.hasOtherSelected = parsedData.hasOtherSelected || false;
                this.otherText = parsedData.otherText || '';
            }
        },
        validateField() {
            const values = this.selectedValues.map(item => item.value);
            if (this.hasOtherSelected && this.otherText) {
                values.push(this.otherText);
            }
            const hasError = this.isRequired && values.length === 0;
            this.$emit("validate", {
                fieldId: this._uid,
                hasError: hasError
            });
        },
        handleOtherChange(event) {
            this.hasOtherSelected = event.target.checked;
            if (!this.hasOtherSelected) {
                this.otherText = "";
            }
            this.emitSelected();
        },
        handleOtherTextChange() {
            if (this.hasOtherSelected) {
                this.emitSelected();
            }
        },
        onFocus(event) {
            event.target.closest('.checkbox-wrapper')?.classList.add('focused');
        },
        onBlur(event) {
            event.target.closest('.checkbox-wrapper')?.classList.remove('focused');
        },
        processItem(item) {
            const div = document.createElement('div');
            div.innerHTML = item;
            const links = div.querySelectorAll('a');
            links.forEach(link => {
                if (!link.hasAttribute('target')) {
                    link.setAttribute('target', '_blank');
                }
            });
            return div.innerHTML;
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(newValue) {
                if (newValue && newValue.length > 0) {
                    this.selectedValues = [];
                    
                    newValue.forEach(val => {
                        if (this.items.includes(val)) {
                            const itemIndex = this.items.findIndex(item => item === val);
                            if (itemIndex !== -1) {
                                this.selectedValues.push({
                                    value: val,
                                    id: this.itemIds[itemIndex]
                                });
                            }
                        }
                    });

                    const otherValue = newValue.find(val => !this.items.includes(val));
                    if (otherValue) {
                        this.hasOtherSelected = true;
                        this.otherText = otherValue;
                    }
                } else {
                    this.selectedValues = [];
                    this.hasOtherSelected = false;
                    this.otherText = "";
                }
                this.validateField();
            }
        },
        showError: {
            handler(newValue) {
                if (newValue) {
                    this.validateField();
                }
            }
        }
    },
    created() {
        this.generateItemIds();
    },
    mounted() {
        const hasSlugValue = this.value && Array.isArray(this.value) && this.value.length > 0;
        
        if (!hasSlugValue) {
            this.loadValues();
        }
        this.validateField();
    }
};
</script>

<style scoped lang="scss">
/* Base MultipleChoice Styles */
.multiple-choice {
    width: 100%;
    max-width: 700px;
    margin: clamp(0.5rem, 1.5vw, 1rem) auto;
    padding: clamp(0.625rem, 1.5vw, 0.875rem);
    position: relative;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    
    background: linear-gradient(145deg, 
        rgba(255, 255, 255, 0.02) 0%,
        rgba(255, 255, 255, 0.01) 100%);
    border-radius: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.06);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
        background: linear-gradient(145deg, 
            rgba(255, 255, 255, 0.03) 0%,
            rgba(255, 255, 255, 0.02) 100%);
    }

    .field-label {
        display: block;
        font-size: clamp(0.95rem, 2.2vw, 1.1rem);
        font-weight: 600;
        margin-bottom: clamp(0.75rem, 1.8vw, 1rem);
        color: var(--text-primary, rgba(55, 65, 81, 0.9));
        line-height: 1.4;
        letter-spacing: -0.01em;
        
        .required-indicator {
            color: #ef4444;
            margin-left: 0.125rem;
            font-weight: 700;
        }
    }

    .input-container {
        position: relative;
        min-width: 0; // Flex item için minimum genişlik
    }

    .checkbox-group {
        display: flex;
        flex-direction: column;
        gap: clamp(0.5rem, 1.2vw, 0.625rem);
    }

    .checkbox-wrapper {
        display: flex;
        align-items: flex-start;
        position: relative;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        padding: clamp(0.5rem, 1.2vw, 0.625rem);
        border-radius: 0.375rem;
        background: rgba(255, 255, 255, 0.3);
        border: 1px solid rgba(209, 213, 219, 0.3);
        
        &:hover {
            background: rgba(255, 255, 255, 0.5);
            border-color: rgba(107, 114, 128, 0.3);
        }

        &.focused {
            background: rgba(255, 255, 255, 0.7);
            border-color: var(--primary, #6366f1);
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        input[type="checkbox"] {
            position: relative;
            width: clamp(1rem, 2.5vw, 1.125rem);
            height: clamp(1rem, 2.5vw, 1.125rem);
            margin: 0;
            margin-right: clamp(0.5rem, 1.2vw, 0.75rem);
            margin-top: 0.125rem;
            appearance: none;
            border: 2px solid rgba(209, 213, 219, 0.8);
            border-radius: 0.25rem;
            background: rgba(255, 255, 255, 0.9);
            cursor: pointer;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            
            &:hover {
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 1);
            }
            
            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
            }
            
            &:checked {
                border-color: var(--primary, #6366f1);
                background: var(--primary, #6366f1);
                
                &::after {
                    content: '';
            position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) rotate(45deg);
                    width: 0.25rem;
                    height: 0.5rem;
                    border: solid white;
                    border-width: 0 2px 2px 0;
        }
    }
        }

        .checkbox-label {
            flex: 1;
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.5;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            cursor: pointer;
            font-weight: 500;
            
            &:hover {
                color: var(--text-primary, rgba(55, 65, 81, 1));
            }
        }

        .other-input {
            margin-top: clamp(0.375rem, 1vw, 0.5rem);
            margin-left: clamp(1.5rem, 3.7vw, 1.875rem);
            width: 100%;
            padding: clamp(0.5rem, 1.2vw, 0.625rem);
            font-size: clamp(0.875rem, 1.8vw, 0.9rem);
            line-height: 1.4;
            color: var(--text-primary, rgba(55, 65, 81, 0.9));
            background: rgba(255, 255, 255, 0.8);
            border: 1px solid rgba(209, 213, 219, 0.6);
            border-radius: 0.375rem;
            box-sizing: border-box;
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
            font-family: inherit;
            backdrop-filter: blur(10px);
            
            &::placeholder {
                color: rgba(107, 114, 128, 0.6);
            }
            
            &:focus {
                outline: none;
                border-color: var(--primary, #6366f1);
                background: rgba(255, 255, 255, 0.95);
                box-shadow: 
                    0 0 0 3px rgba(99, 102, 241, 0.1),
                    0 2px 8px rgba(0, 0, 0, 0.05);
            }
            
            &:hover:not(:focus) {
                border-color: rgba(107, 114, 128, 0.4);
                background: rgba(255, 255, 255, 0.85);
            }
        }
    }

    .error-message {
        color: #ef4444;
        font-size: clamp(0.8rem, 1.8vw, 0.875rem);
        margin-top: clamp(0.5rem, 1.2vw, 0.75rem);
        font-weight: 500;
        display: flex;
        align-items: center;
        gap: 0.375rem;
        
        &::before {
            content: '⚠';
            font-size: 0.875em;
        }
    }
}

/* Left Alignment - Label solda, checkbox grup sağda */
.multiple-choice-align-left {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);
    text-align: left;

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.5rem, 1.2vw, 0.625rem);
        text-align: left;
    }

    .input-container {
        flex: 1;
    }

    .checkbox-group {
        gap: clamp(0.375rem, 1vw, 0.5rem);
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.75rem, 1.8vw, 1rem);
        }
        
        .input-container {
            width: 100%;
        }
        
        .checkbox-group {
            gap: clamp(0.5rem, 1.2vw, 0.625rem);
    }
}
}

/* Right Alignment - Label solda sağa yaslanmış, checkbox grup sağda */
.multiple-choice-align-right {
    display: flex;
    align-items: flex-start;
    gap: clamp(0.75rem, 2vw, 1rem);

    .field-label {
        min-width: clamp(100px, 20vw, 140px);
        flex-shrink: 0;
        margin-bottom: 0;
        padding-top: clamp(0.5rem, 1.2vw, 0.625rem);
        text-align: right;
    }

    .input-container {
        flex: 1;
    }

    .checkbox-group {
        gap: clamp(0.375rem, 1vw, 0.5rem);
    }
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 0;
        
        .field-label {
            min-width: auto;
            padding-top: 0;
            margin-bottom: clamp(0.75rem, 1.8vw, 1rem);
            text-align: left;
        }
        
        .input-container {
        width: 100%;
        }
        
        .checkbox-group {
            gap: clamp(0.5rem, 1.2vw, 0.625rem);
    }
}
}

/* Center/Top Alignment (Default) */
.multiple-choice-align-top {
    text-align: left;
    flex-direction: column;
}

// Mobil optimizasyonları
@media (max-width: 768px) {
    .multiple-choice {
        margin: clamp(0.25rem, 1vw, 0.5rem) auto;
        padding: clamp(0.5rem, 1.2vw, 0.75rem);
        
        .field-label {
            font-size: clamp(0.9rem, 2vw, 1rem);
            margin-bottom: clamp(0.625rem, 1.5vw, 0.875rem);
        }

    .checkbox-group {
            gap: clamp(0.375rem, 1vw, 0.5rem);
        }
        
        .checkbox-wrapper {
            padding: clamp(0.5rem, 1.2vw, 0.625rem);
            
            input[type="checkbox"] {
                width: clamp(1rem, 2.5vw, 1.125rem);
                height: clamp(1rem, 2.5vw, 1.125rem);
                margin-right: clamp(0.5rem, 1.2vw, 0.75rem);
            }
            
            .checkbox-label {
                font-size: clamp(0.875rem, 2vw, 0.9rem);
            }
            
            .other-input {
                margin-top: clamp(0.375rem, 1vw, 0.5rem);
                margin-left: clamp(1.5rem, 3.7vw, 1.875rem);
                padding: clamp(0.5rem, 1.2vw, 0.625rem);
                font-size: clamp(0.875rem, 2vw, 0.9rem);
                border-radius: 0.5rem;
            }
        }
        
        .error-message {
            font-size: clamp(0.8rem, 1.8vw, 0.85rem);
            margin-top: clamp(0.375rem, 1vw, 0.5rem);
        }
    }
}

@media (max-width: 480px) {
    .multiple-choice {
        margin: 0.25rem auto;
        padding: 0.5rem;
        
        .field-label {
            font-size: 0.9rem;
            margin-bottom: 0.625rem;
        }
        
        .checkbox-group {
            gap: 0.375rem;
    }

    .checkbox-wrapper {
            padding: 0.5rem;

        input[type="checkbox"] {
                width: 1rem;
                height: 1rem;
                margin-right: 0.5rem;
        }

            .checkbox-label {
                font-size: 0.875rem;
            }
            
            .other-input {
                margin-top: 0.375rem;
                margin-left: 1.5rem;
                padding: 0.5rem;
                font-size: 0.875rem;
            }
        }
        
        .error-message {
            font-size: 0.8rem;
            margin-top: 0.375rem;
        }
    }
}

/* Dark mode devre dışı - Her zaman açık tema kullan */

/* Reduced Motion Support */
@media (prefers-reduced-motion: reduce) {
    .multiple-choice,
    .checkbox-wrapper,
    .checkbox-wrapper input,
    .checkbox-label,
    .other-input {
        transition: none;
    }
}

/* High Performance Mode */
.checkbox-wrapper {
    will-change: transform, background-color, border-color;
    
    input[type="checkbox"] {
        will-change: border-color, background-color, box-shadow;
    }
    
    .other-input {
        will-change: border-color, background-color, box-shadow;
    }
}
</style>